/**
 * Shared Firestore compat helpers for Lectura Next services.
 * Uses the same Firebase config precedence as plugin/lectura.
 */

import { loadFirebaseAuthScripts } from './auth-firebase-load';

export interface FirestoreServiceOptions {
  firebaseConfig?: unknown;
  chatConfig?: { firebaseConfig?: unknown } | null;
}

interface FirestoreDocSnapshot {
  id: string;
  data(): Record<string, unknown>;
}

interface FirestoreQuerySnapshot {
  docs: FirestoreDocSnapshot[];
}

interface FirestoreDocRef {
  collection(path: string): FirestoreCollectionRef;
}

interface FirestoreCollectionRef {
  doc(id?: string): FirestoreDocRef;
  add(data: Record<string, unknown>): Promise<unknown>;
  onSnapshot(
    cb: (snap: FirestoreQuerySnapshot) => void,
    errCb?: (err: unknown) => void
  ): () => void;
}

interface FirestoreDb {
  collection(path: string): FirestoreCollectionRef;
}

interface FirebaseCompat {
  apps?: Array<unknown>;
  app?: (name?: string) => unknown;
  initializeApp?: (config: unknown) => unknown;
  firestore?: (() => FirestoreDb) & {
    FieldValue?: {
      serverTimestamp?: () => unknown;
    };
  };
}

declare global {
  interface Window {
    firebase?: FirebaseCompat;
    __LECTURA_FIREBASE_CONFIG__?: unknown;
  }
}

export function resolveFirebaseConfig(opts: FirestoreServiceOptions): unknown {
  return opts.firebaseConfig ?? opts.chatConfig?.firebaseConfig ?? (typeof window !== 'undefined' ? window.__LECTURA_FIREBASE_CONFIG__ : null) ?? null;
}

export async function ensureFirestoreDb(opts: FirestoreServiceOptions): Promise<FirestoreDb> {
  await loadFirebaseAuthScripts();
  const fb = typeof window !== 'undefined' ? window.firebase : undefined;
  if (!fb || typeof fb.firestore !== 'function') {
    throw new Error('Firebase Firestore is not available');
  }
  if (!fb.apps || fb.apps.length === 0) {
    const cfg = resolveFirebaseConfig(opts);
    if (!cfg || typeof cfg !== 'object') throw new Error('Missing firebaseConfig');
    if (typeof fb.initializeApp !== 'function') throw new Error('Firebase initializeApp is unavailable');
    fb.initializeApp(cfg);
  }
  return fb.firestore();
}

export function getMessagesCollection(db: FirestoreDb, presId: string, slideIndex: number): FirestoreCollectionRef {
  return db
    .collection('presentations')
    .doc(String(presId))
    .collection('slides')
    .doc(String(slideIndex))
    .collection('messages');
}

export function getUserSlideNoteDoc(db: FirestoreDb, uid: string, presId: string, slideIndex: number): FirestoreDocRef {
  return db
    .collection('users')
    .doc(String(uid))
    .collection('presentations')
    .doc(String(presId))
    .collection('slideNotes')
    .doc(String(slideIndex));
}

export function getUserRatingDoc(db: FirestoreDb, uid: string, presId: string): FirestoreDocRef {
  return db.collection('users').doc(String(uid)).collection('ratings').doc(String(presId));
}

export function getPresentationRatingDoc(db: FirestoreDb, uid: string, presId: string): FirestoreDocRef {
  return db.collection('presentations').doc(String(presId)).collection('ratings').doc(String(uid));
}
